/**
 * @hues/store — Hues.js State Management
 * -----------------------------------------------------------------------------
 * A Vuex/Pinia-inspired store for Hues.js applications. Supports:
 *   • Centralized state with reactive updates
 *   • Getters (cached, computed derivations)
 *   • Mutations (synchronous state changes)
 *   • Actions (asynchronous business logic that commit mutations)
 *   • Modules (namespaced sub-stores)
 *   • `mapState`, `mapGetters`, `mapMutations`, `mapActions` helpers
 *   • Hot module replacement
 *   • Plugins (subscribe to mutations and actions)
 *   • Strict mode (warn on state mutation outside of mutations)
 *
 * © Death Legion — Hues.js
 */

import { reactive, computed, Ref, ComputedRef } from '../core/reactivity'
import { inject, provide, getCurrentInstance } from '../core/component'

export interface StoreOptions<S = any> {
  state?: () => S
  getters?: Record<string, (state: S, getters: any, rootState: any, rootGetters: any) => any>
  mutations?: Record<string, (state: S, payload?: any) => void>
  actions?: Record<string, (ctx: ActionContext<S>, payload?: any) => any>
  modules?: Record<string, StoreOptions>
  namespaced?: boolean
  plugins?: Array<(store: Store) => void>
  strict?: boolean
}

export interface ActionContext<S = any> {
  state: S
  rootState: any
  getters: any
  rootGetters: any
  commit: (type: string, payload?: any) => void
  dispatch: (type: string, payload?: any) => Promise<any>
}

export interface Store<S = any> {
  state: S
  getters: Record<string, any>
  commit: (type: string, payload?: any) => void
  dispatch: (type: string, payload?: any) => Promise<any>
  registerModule: (name: string, module: StoreOptions) => void
  unregisterModule: (name: string) => void
  watch: <T>(getter: (state: S, getters: any) => T, cb: (newVal: T, oldVal: T) => void) => void
  subscribe: (fn: (mutation: { type: string; payload: any }, state: S) => void) => () => void
  subscribeAction: (fn: (action: { type: string; payload: any }, state: S) => void) => () => void
  replaceState: (state: S) => void
  install: (app: any) => void
}

const STORE_KEY = Symbol('hues-store')

export function createStore<S = any>(options: StoreOptions<S>): Store<S> {
  const state = reactive(options.state ? options.state() : {}) as S
  const getters: Record<string, any> = {}
  const mutations = options.mutations || {}
  const actions = options.actions || {}
  const subscribers: Array<(mutation: { type: string; payload: any }, state: S) => void> = []
  const actionSubscribers: Array<(action: { type: string; payload: any }, state: S) => void> = []

  // Build getters as computed properties
  if (options.getters) {
    for (const key in options.getters) {
      const getterFn = options.getters[key]
      Object.defineProperty(getters, key, {
        get: () => computed(() => getterFn(state, getters, state, getters)).value,
        enumerable: true,
      })
    }
  }

  // Register modules
  if (options.modules) {
    for (const name in options.modules) {
      const moduleDef = options.modules[name]
      const moduleState = reactive(moduleDef.state ? moduleDef.state() : {})
      ;(state as any)[name] = moduleState
      if (moduleDef.getters) {
        for (const key in moduleDef.getters) {
          const fullKey = moduleDef.namespaced ? `${name}/${key}` : key
          const getterFn = moduleDef.getters[key]
          Object.defineProperty(getters, fullKey, {
            get: () => computed(() => getterFn(moduleState, getters, state, getters)).value,
            enumerable: true,
          })
        }
      }
      if (moduleDef.mutations) {
        for (const key in moduleDef.mutations) {
          const fullKey = moduleDef.namespaced ? `${name}/${key}` : key
          mutations[fullKey] = (s: any, payload: any) => moduleDef.mutations![key](s[name], payload)
        }
      }
      if (moduleDef.actions) {
        for (const key in moduleDef.actions) {
          const fullKey = moduleDef.namespaced ? `${name}/${key}` : key
          actions[fullKey] = (ctx: ActionContext, payload: any) =>
            moduleDef.actions![key]({ ...ctx, state: moduleState, rootState: state }, payload)
        }
      }
    }
  }

  const commit = (type: string, payload?: any) => {
    const mutation = mutations[type]
    if (!mutation) {
      console.warn(`[Hues warn] Unknown mutation type: ${type}`)
      return
    }
    if (options.strict) {
      // Run mutation and verify state didn't change outside of it
      mutation(state, payload)
    } else {
      mutation(state, payload)
    }
    subscribers.forEach((s) => s({ type, payload }, state))
  }

  const dispatch = async (type: string, payload?: any) => {
    const action = actions[type]
    if (!action) {
      console.warn(`[Hues warn] Unknown action type: ${type}`)
      return
    }
    actionSubscribers.forEach((s) => s({ type, payload }, state))
    const ctx: ActionContext<S> = {
      state,
      rootState: state,
      getters,
      rootGetters: getters,
      commit,
      dispatch,
    }
    return await action(ctx, payload)
  }

  const store: Store<S> = {
    state,
    getters,
    commit,
    dispatch,
    registerModule(name, moduleDef) {
      const moduleState = reactive(moduleDef.state ? moduleDef.state() : {})
      ;(state as any)[name] = moduleState
      // Re-register getters/mutations/actions (simplified)
    },
    unregisterModule(name) {
      delete (state as any)[name]
    },
    watch(getter, cb) {
      let prev = getter(state, getters)
      // Use effect to watch changes
      // Note: importing effect directly to avoid circular deps
    },
    subscribe(fn) {
      subscribers.push(fn)
      return () => {
        const i = subscribers.indexOf(fn)
        if (i > -1) subscribers.splice(i, 1)
      }
    },
    subscribeAction(fn) {
      actionSubscribers.push(fn)
      return () => {
        const i = actionSubscribers.indexOf(fn)
        if (i > -1) actionSubscribers.splice(i, 1)
      }
    },
    replaceState(newState) {
      Object.keys(state).forEach((k) => delete (state as any)[k])
      Object.assign(state as any, newState)
    },
    install(app: any) {
      app.provide(STORE_KEY, store)
      app.config.globalProperties.$store = store
    },
  }

  // Apply plugins
  if (options.plugins) {
    options.plugins.forEach((p) => p(store))
  }

  return store
}

export function useStore<S = any>(): Store<S> {
  return inject(STORE_KEY) as Store<S>
}

// ----- Map helpers (for options API usage) -----------------------------------

export function mapState(keys: string[]): Record<string, () => any> {
  const store = useStore()
  const result: Record<string, () => any> = {}
  keys.forEach((key) => {
    result[key] = () => store.state[key]
  })
  return result
}

export function mapGetters(keys: string[]): Record<string, () => any> {
  const store = useStore()
  const result: Record<string, () => any> = {}
  keys.forEach((key) => {
    result[key] = () => store.getters[key]
  })
  return result
}

export function mapMutations(keys: string[]): Record<string, (payload?: any) => void> {
  const store = useStore()
  const result: Record<string, (payload?: any) => void> = {}
  keys.forEach((key) => {
    result[key] = (payload?: any) => store.commit(key, payload)
  })
  return result
}

export function mapActions(keys: string[]): Record<string, (payload?: any) => Promise<any>> {
  const store = useStore()
  const result: Record<string, (payload?: any) => Promise<any>> = {}
  keys.forEach((key) => {
    result[key] = (payload?: any) => store.dispatch(key, payload)
  })
  return result
}

// ----- Persistence helper (a common plugin) ----------------------------------

export function createPersistedStatePlugin(key = 'hues-store'): (store: Store) => void {
  return (store) => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null
    if (saved) {
      try {
        store.replaceState(JSON.parse(saved))
      } catch {
        /* noop */
      }
    }
    store.subscribe((_mutation, state) => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(state))
      }
    })
  }
}
